// src/components/timeBoxes/TaskSelector/TaskSelectorInline.tsx
// Inline 展開式任務選擇器 - 統一桌面與手機體驗

import React, { useState, useRef, useCallback } from 'react';
import useClickOutside from '../../../../hooks/useClickOutside';
import { Plus } from '../../../../components/common/icons';
import { Task } from '../../../../types/task';
import { useTaskSelector } from './useTaskSelector';
import { TaskListItem } from './TaskListItem';

interface TaskSelectorInlineProps {
  onSelectTask: (taskId: string) => void;
  excludeTaskIds: string[];
  relatedTasks: Task[];
  userId: string;
}

/**
 * Inline 任務選擇器
 *
 * 特性：
 * - 按鈕原地展開為搜尋框（cross-fade 動畫）
 * - 結果列表從搜尋框下方滑入
 * - Click-outside 自動關閉
 * - 統一體驗（桌面與手機無差異）
 */
export const TaskSelectorInline: React.FC<TaskSelectorInlineProps> = ({
  onSelectTask,
  excludeTaskIds,
  relatedTasks,
  userId
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    filteredTasks,
    searchQuery,
    setSearchQuery,
    preferredCategories
  } = useTaskSelector({ excludeTaskIds, relatedTasks, userId });

  // 關閉並重置狀態
  const handleClose = useCallback(() => {
    setIsExpanded(false);
    setSearchQuery('');
  }, [setSearchQuery]);

  // 選擇任務
  const handleSelectTask = useCallback((taskId: string) => {
    onSelectTask(taskId);
    handleClose();
  }, [onSelectTask, handleClose]);

  // Esc 鍵關閉
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleClose();
    }
  }, [handleClose]);

  useClickOutside(containerRef, handleClose, isExpanded);

  return (
    <div ref={containerRef} className="relative">
      {/* 收合狀態：按鈕 */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className="flex items-center text-sm text-sky-600 hover:text-sky-800 font-medium py-1 transition-colors"
        >
          <Plus size={16} className="mr-1.5" />
          Add related task
        </button>
      )}

      {/* 展開狀態：搜尋框 + 結果列表 */}
      {isExpanded && (
        <div className="relative">
            {/* 搜尋框（精簡版，無外層 padding 和 border） */}
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search tasks"
                className="w-full md:min-w-[400px] px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
                autoFocus
                aria-label="Search tasks"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* 結果下拉列表（展開時即顯示） */}
            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-dropdown max-h-[320px] overflow-y-auto">
              {/* 智能推薦提示（僅在未搜尋時顯示） */}
              {searchQuery.length === 0 && preferredCategories.length > 0 && (
                <div className="px-4 py-2 bg-sky-50 border-b border-sky-100">
                  <p className="text-xs text-sky-700 font-medium">
                    💡 Showing recommended tasks
                  </p>
                </div>
              )}

              {filteredTasks.length === 0 ? (
                <div className="px-4 py-8 text-center text-gray-400 text-sm">
                  <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p>No tasks found</p>
                  {searchQuery.length > 0 && (
                    <p className="text-xs mt-1">Try a different search term</p>
                  )}
                </div>
              ) : (
                filteredTasks.slice(0, searchQuery.length === 0 ? 5 : 10).map((task) => (
                  <TaskListItem
                    key={task.id}
                    task={task}
                    isSelected={false}
                    onClick={() => handleSelectTask(task.id)}
                  />
                ))
              )}
            </div>
          </div>
        )}
    </div>
  );
};
